<template>
  <v-card variant="outlined" class="pa-3">
    <div class="d-flex align-center mb-3">
      <v-icon color="secondary" class="mr-2">mdi-filter-variant</v-icon>
      <span class="text-subtitle-1 font-weight-medium">Criterios de Selección</span>
      <v-spacer></v-spacer>
      <v-chip 
        v-if="hasValidSelection" 
        color="success" 
        size="small"
        prepend-icon="mdi-check"
      >
        Seleccionado
      </v-chip>
    </div>
    
    <v-row>
      <!-- Selector de Tipo de Criterio -->
      <v-col cols="12" md="3">
        <v-select
          v-model="criteria.type"
          :items="getAvailableCriteriaTypes()"
          item-title="text"
          item-value="value"
          label="Criterio de Selección"
          @update:model-value="onCriteriaTypeChange"
          :disabled="!hasAnyCriteria"
          clearable
          variant="outlined"
          density="compact"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props">
              <template v-slot:prepend>
                <v-icon :icon="item.raw.icon" color="primary"></v-icon>
              </template>
              <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.raw.count }} disponible(s)</v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      
      <!-- Selector de Lote -->
      <v-col cols="12" md="3" v-if="criteria.type === 'lot'">
        <v-select
          v-model="criteria.value"
          :items="availableLots"
          item-title="text"
          item-value="value"
          label="Seleccionar Lote"
          @update:model-value="onCriteriaValueChange"
          :disabled="isLotDisabled"
          variant="outlined"
          density="compact"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props" :disabled="item.raw.disabled">
              <template v-slot:prepend>
                <v-icon 
                  :icon="item.raw.disabled ? 'mdi-lock' : 'mdi-package-variant'" 
                  :color="item.raw.disabled ? 'error' : 'primary'"
                ></v-icon>
              </template>
              <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
              <v-list-item-subtitle>
                <span v-if="item.raw.disabled" class="text-error">Ya usado en otra línea</span>
                <span v-else>{{ item.raw.quantity }} unidades disponibles</span>
              </v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      
      <!-- Selector de S/N -->
      <v-col cols="12" md="3" v-if="criteria.type === 'serial'">
        <v-select
          v-model="criteria.value"
          :items="availableSerials"
          item-title="text"
          item-value="value"
          label="Seleccionar S/N"
          @update:model-value="onCriteriaValueChange"
          variant="outlined"
          density="compact"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props">
              <template v-slot:prepend>
                <v-icon icon="mdi-barcode" color="primary"></v-icon>
              </template>
              <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.raw.location.code }}</v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      
      <!-- Selector de Fecha de Vencimiento -->
      <v-col cols="12" md="3" v-if="criteria.type === 'expiry'">
        <v-select
          v-model="criteria.value"
          :items="availableExpiryDates"
          item-title="text"
          item-value="value"
          label="Seleccionar Fecha Venc."
          @update:model-value="onCriteriaValueChange"
          variant="outlined"
          density="compact"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props">
              <template v-slot:prepend>
                <v-icon 
                  :icon="getExpiryIcon(item.raw.value)" 
                  :color="getExpiryColor(item.raw.value)"
                ></v-icon>
              </template>
              <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
              <v-list-item-subtitle>{{ getDaysToExpiry(item.raw.value) }}</v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      
      <!-- Información adicional -->
      <v-col cols="12" md="3" v-if="criteria.type">
        <v-text-field
          :model-value="criteria.available_quantity"
          label="Cantidad Disponible"
          readonly
          variant="outlined"
          density="compact"
          suffix="unidades"
          prepend-inner-icon="mdi-package"
        ></v-text-field>
      </v-col>
    </v-row>
    
    <!-- Alertas informativas -->
    <div class="mt-3">
      <!-- Sin criterios disponibles -->
      <v-alert 
        v-if="!hasAnyCriteria" 
        type="warning" 
        variant="tonal" 
        icon="mdi-alert-circle"
        class="mb-2"
      >
        <div class="font-weight-medium">Sin criterios de trazabilidad</div>
        <div class="text-body-2">
          Este producto no tiene lotes, números de serie o fechas de vencimiento registrados.
        </div>
      </v-alert>
      
      <!-- Información de disponibilidad -->
      <v-alert 
        v-if="hasValidSelection" 
        type="success" 
        variant="tonal" 
        icon="mdi-check-circle"
        class="mb-2"
      >
        <div class="font-weight-medium">Criterio seleccionado</div>
        <div class="text-body-2">
          {{ getSelectionSummary() }}
        </div>
      </v-alert>
      
      <!-- Error de validación -->
      <v-alert 
        v-if="errorMessage" 
        type="error" 
        variant="tonal" 
        icon="mdi-alert"
        class="mb-2"
      >
        <div class="font-weight-medium">Error de validación</div>
        <div class="text-body-2">{{ errorMessage }}</div>
      </v-alert>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'PickingCriteriaSelector',
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        type: null,
        value: null,
        available_quantity: 0
      })
    },
    availableLots: {
      type: Array,
      default: () => []
    },
    availableSerials: {
      type: Array,
      default: () => []
    },
    availableExpiryDates: {
      type: Array,
      default: () => []
    },
    usedLots: {
      type: Array,
      default: () => []
    },
    errorMessage: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue', 'criteria-changed'],
  
  data() {
    return {
      criteria: { ...this.modelValue }
    }
  },
  
  computed: {
    hasAnyCriteria() {
      return this.availableLots.length > 0 || 
             this.availableSerials.length > 0 || 
             this.availableExpiryDates.length > 0
    },
    
    hasValidSelection() {
      return this.criteria.type && this.criteria.value && this.criteria.available_quantity > 0
    },
    
    isLotDisabled() {
      return this.availableLots.length === 0
    }
  },
  
  watch: {
    modelValue: {
      handler(newValue) {
        console.log(`🔄 PickingCriteriaSelector received new modelValue:`, newValue)
        if (JSON.stringify(newValue) !== JSON.stringify(this.criteria)) {
          this.criteria = { ...newValue }
          console.log(`📋 PickingCriteriaSelector updated criteria:`, this.criteria)
        }
      },
      deep: true,
      immediate: true
    },
    
    availableLots: {
      handler(newLots) {
        console.log(`📦 PickingCriteriaSelector received availableLots:`, newLots)
      },
      immediate: true
    }
  },
  
  methods: {
    getAvailableCriteriaTypes() {
      const types = []
      
      if (this.availableLots.length > 0) {
        types.push({
          text: 'Por Lote',
          value: 'lot',
          icon: 'mdi-package-variant',
          count: this.availableLots.length
        })
      }
      
      if (this.availableSerials.length > 0) {
        types.push({
          text: 'Por S/N',
          value: 'serial',
          icon: 'mdi-barcode',
          count: this.availableSerials.length
        })
      }
      
      if (this.availableExpiryDates.length > 0) {
        types.push({
          text: 'Por Fecha Venc.',
          value: 'expiry',
          icon: 'mdi-calendar-clock',
          count: this.availableExpiryDates.length
        })
      }
      
      return types
    },
    
    onCriteriaTypeChange(newType) {
      this.criteria.type = newType
      this.criteria.value = null
      this.criteria.available_quantity = 0
      
      // Emitir cambio de modelo
      this.$emit('update:modelValue', { ...this.criteria })
      
      this.$emit('criteria-changed', {
        type: 'type-changed',
        criteriaType: newType
      })
    },
    
    onCriteriaValueChange(newValue) {
      let selectedItem = null
      let quantity = 0
      
      switch (this.criteria.type) {
        case 'lot':
          selectedItem = this.availableLots.find(lot => lot.value === newValue)
          break
        case 'serial':
          selectedItem = this.availableSerials.find(serial => serial.value === newValue)
          break
        case 'expiry':
          selectedItem = this.availableExpiryDates.find(expiry => expiry.value === newValue)
          break
      }
      
      if (selectedItem) {
        quantity = selectedItem.quantity || 0
      }
      
      this.criteria.value = newValue
      this.criteria.available_quantity = quantity
      
      // Emitir cambio de modelo
      this.$emit('update:modelValue', { ...this.criteria })
      
      this.$emit('criteria-changed', {
        type: 'value-changed',
        criteriaType: this.criteria.type,
        criteriaValue: newValue,
        availableQuantity: quantity,
        selectedItem: selectedItem
      })
    },
    
    getExpiryIcon(expiryDate) {
      const daysToExpiry = this.calculateDaysToExpiry(expiryDate)
      if (daysToExpiry <= 7) return 'mdi-alert-circle'
      if (daysToExpiry <= 30) return 'mdi-clock-alert'
      return 'mdi-calendar-check'
    },
    
    getExpiryColor(expiryDate) {
      const daysToExpiry = this.calculateDaysToExpiry(expiryDate)
      if (daysToExpiry <= 7) return 'error'
      if (daysToExpiry <= 30) return 'warning'
      return 'success'
    },
    
    getDaysToExpiry(expiryDate) {
      const days = this.calculateDaysToExpiry(expiryDate)
      if (days < 0) return 'Vencido'
      if (days === 0) return 'Vence hoy'
      if (days === 1) return 'Vence mañana'
      return `${days} días para vencer`
    },
    
    calculateDaysToExpiry(expiryDate) {
      const today = new Date()
      const expiry = new Date(expiryDate)
      const diffTime = expiry - today
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    },
    
    getSelectionSummary() {
      if (!this.hasValidSelection) return ''
      
      const typeText = this.getAvailableCriteriaTypes()
        .find(t => t.value === this.criteria.type)?.text || ''
      
      return `${typeText}: ${this.criteria.value} (${this.criteria.available_quantity} unidades disponibles)`
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: border-color 0.3s ease;
}

.v-card:hover {
  border-color: rgb(var(--v-theme-primary));
}

.v-alert {
  margin-bottom: 0;
}
</style> 